import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Building2, MapPin, User, Users, Phone, Mail, Globe, Briefcase,
  FileText, Upload, Check, ChevronRight, ChevronLeft, ChevronDown,
  Plus, Trash2, Loader2, Building, DollarSign, Shield,
  AlertCircle, CheckCircle, Image, Linkedin, Calendar, ArrowLeft, Save, X
} from 'lucide-react';
import { Country } from 'country-state-city';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { MultiSelectDropdown } from './ui/multi-select-dropdown';
import { SearchableSelect } from './ui/searchable-select';
import { toast } from 'sonner';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { LOGO_URL } from '../constants/brand';
import {
  getCityOptionsForCountry,
  getCountryIsoCode,
  getPhoneCodeForCountry,
  getStateIsoCode,
  getStateOptionsForCountry,
  useCanonicalCountryOptions,
} from '../utils/countries';

// Use the shared API client base URL so live hosts stay same-origin.
const API_URL = '';

function normalizePhoneCode(phonecode) {
  if (!phonecode) return '+1';
  const part = String(phonecode).split('-')[0].trim();
  return part.startsWith('+') ? part : `+${part}`;
}

function getEmailDomain(email) {
  const value = String(email || '').trim().toLowerCase();
  const parts = value.split('@');
  return parts.length === 2 ? parts[1] : '';
}

const COUNTRIES = [
  'United States', 'United Kingdom', 'Germany', 'France', 'India', 'China',
  'Japan', 'UAE', 'Singapore', 'Australia', 'Brazil', 'Mexico', 'Netherlands',
  'Spain', 'Italy', 'Canada', 'South Korea', 'Indonesia', 'Thailand', 'Vietnam',
  'Malaysia', 'Philippines', 'Hong Kong', 'Taiwan', 'Saudi Arabia', 'Qatar',
  'South Africa', 'Nigeria', 'Egypt', 'Turkey', 'Poland', 'Belgium', 'Sweden',
  'Switzerland', 'Austria', 'Norway', 'Denmark', 'Finland', 'Ireland', 'Portugal',
  'Greece', 'Czech Republic', 'Romania', 'Hungary', 'New Zealand', 'Argentina',
  'Chile', 'Colombia', 'Peru', 'Pakistan', 'Bangladesh', 'Sri Lanka', 'Kenya'
];

const CURRENCIES = ['USD', 'EUR', 'GBP', 'INR', 'CNY', 'JPY', 'AED', 'SGD', 'AUD', 'BRL'];
const MIN_INDUSTRY_REFERENCES = 2;

const BRANCH_TYPES = ['Regional Office', 'Sales Office', 'Warehouse', 'Distribution Center', 'Representative Office'];
const TRADE_LANE_OPTIONS = [
  { value: 'Asia <-> North America', label: 'Asia <-> North America' },
  { value: 'Asia <-> Europe', label: 'Asia <-> Europe' },
  { value: 'Transatlantic (Europe <-> North America)', label: 'Transatlantic (Europe <-> North America)' },
  { value: 'China <-> Southeast Asia', label: 'China <-> Southeast Asia' },
  { value: 'India <-> China', label: 'India <-> China' },
  { value: 'Japan/Korea <-> ASEAN', label: 'Japan/Korea <-> ASEAN' },
  { value: 'Asia <-> Middle East', label: 'Asia <-> Middle East' },
  { value: 'Asia <-> Africa', label: 'Asia <-> Africa' },
  { value: 'Europe <-> Africa', label: 'Europe <-> Africa' },
  { value: 'North America <-> Latin America', label: 'North America <-> Latin America' },
  { value: 'India <-> USA', label: 'India <-> USA' },
  { value: 'India <-> Europe', label: 'India <-> Europe' },
  { value: 'China <-> Latin America', label: 'China <-> Latin America' },
  { value: 'Middle East <-> Africa', label: 'Middle East <-> Africa' },
  { value: 'Asia <-> Oceania', label: 'Asia <-> Oceania' },
  { value: 'Oceania <-> North America', label: 'Oceania <-> North America' },
  { value: 'Oceania <-> Europe', label: 'Oceania <-> Europe' },
  { value: 'Oceania <-> Middle East', label: 'Oceania <-> Middle East' },
  { value: 'Oceania <-> Africa', label: 'Oceania <-> Africa' },
  { value: 'Intra Oceania Trade', label: 'Intra Oceania Trade' },
];

const LANGUAGES_SPOKEN = [
  'English', 'Spanish', 'French', 'German', 'Mandarin', 'Arabic', 'Portuguese',
  'Russian', 'Japanese', 'Hindi', 'Italian', 'Dutch', 'Korean', 'Turkish',
  'Vietnamese', 'Thai', 'Indonesian', 'Malay', 'Polish', 'Swedish', 'Danish',
  'Norwegian', 'Greek', 'Czech', 'Romanian', 'Hungarian', 'Hebrew', 'Bengali',
  'Urdu', 'Punjabi', 'Cantonese', 'Other'
];

const COMPANY_STRENGTHS = [
  'Ocean Freight – FCL',
  'Ocean Freight – LCL',
  'Air Freight – General Cargo',
  'Air Freight – Express / Courier',
  'Breakbulk / Project Cargo',
  'RoRo (Roll-on/Roll-off)',
  'EPC / Turnkey Logistics',
  'Multimodal / Cross-border',
  'Dangerous Goods (DG Certified)',
  'Reefer Cargo (Frozen/Chilled)',
  'High Value Cargo (secured handling)',
  'Customs Clearance (Origin/Destination)',
  'Warehousing & Distribution',
  'Africa Trade Lanes',
  'Middle East Trade',
  'Europe Trade',
  'USA / Americas Trade',
  'Far East / China Specialist',
  'Intra-Asia',
  'Cross-border Trucking',
  'Cargo Insurance',
  'Packaging / Crating',
  'Cargo Survey & Inspection',
  'Chartering (Air / Vessel)',
  'AEO Certified',
  'IATA Accredited',
  'FIATA Member',
  'ISO Certified',
  'DG Certified Staff',
];

const createEmptyTradeReference = () => ({
  company_name: '',
  contact_person: '',
  email: '',
  country_code: '+1',
  mobile: '',
  relationship_duration: ''
});

// Head Office Address: Country -> State -> City + Postal code, with searchable dropdowns and "Others"
function HeadOfficeAddressSection({ companyInfo, setCompanyInfo, fieldErrors, FieldError }) {
  const countryList = useCanonicalCountryOptions();
  const [isCustomState, setIsCustomState] = useState(false);
  const [isCustomCity, setIsCustomCity] = useState(false);

  const countryIsoCode = useMemo(() => {
    return getCountryIsoCode(companyInfo.country);
  }, [companyInfo.country]);

  const stateList = useMemo(() => {
    return getStateOptionsForCountry(companyInfo.country);
  }, [companyInfo.country]);

  const stateIsOther = !!(companyInfo.state_region && !stateList.some((s) => s.value === companyInfo.state_region));
  const stateSelectValue = (isCustomState || stateIsOther) ? '__other__' : (companyInfo.state_region || '');

  const stateIsoCode = useMemo(() => {
    if (!countryIsoCode || !companyInfo.state_region || stateIsOther) return null;
    return getStateIsoCode(companyInfo.country, companyInfo.state_region);
  }, [countryIsoCode, companyInfo.country, companyInfo.state_region, stateIsOther]);

  const cityList = useMemo(() => {
    if (!countryIsoCode) return [];
    return getCityOptionsForCountry(companyInfo.country, (isCustomState || stateIsOther) ? null : companyInfo.state_region);
  }, [countryIsoCode, companyInfo.country, companyInfo.state_region, isCustomState, stateIsOther]);

  const cityIsOther = !!(companyInfo.city && !cityList.some((c) => c.value === companyInfo.city));
  const citySelectValue = (isCustomCity || cityIsOther) ? '__other__' : (companyInfo.city || '');

  useEffect(() => {
    setIsCustomState(stateIsOther);
  }, [stateIsOther]);

  useEffect(() => {
    setIsCustomCity(cityIsOther);
  }, [cityIsOther]);

  const handleCountryChange = (value) => {
    const code = getPhoneCodeForCountry(value || '');
    setIsCustomState(false);
    setIsCustomCity(false);
    setCompanyInfo((prev) => ({
      ...prev,
      country: value || '',
      state_region: '',
      city: '',
      primary_contact_country_code: code,
      secondary_contact_country_code: code,
    }));
  };

  const handleStateChange = (value) => {
    if (value === '__other__') {
      setIsCustomState(true);
      setIsCustomCity(false);
      setCompanyInfo((prev) => ({ ...prev, state_region: '', city: '' }));
      return;
    }
    setIsCustomState(false);
    setIsCustomCity(false);
    setCompanyInfo((prev) => ({ ...prev, state_region: value || '', city: '' }));
  };

  const handleCityChange = (value) => {
    if (value === '__other__') {
      setIsCustomCity(true);
      setCompanyInfo((prev) => ({ ...prev, city: '' }));
      return;
    }
    setIsCustomCity(false);
    setCompanyInfo((prev) => ({ ...prev, city: value || '' }));
  };

  return (
    <Card className="mt-6 border-t border-border/80">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center text-lg">
          <MapPin size={20} className="mr-2 text-blue-600" />
          Head Office Address
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label>Street Address <span className="text-red-500">*</span></Label>
          <Input
            value={companyInfo.street_address}
            onChange={(e) => setCompanyInfo({ ...companyInfo, street_address: e.target.value })}
            placeholder="Address Line 1"
            className={fieldErrors.street_address ? 'border-red-500' : ''}
          />
          <FieldError fieldKey="street_address" />
        </div>
        <div className="md:col-span-2">
          <Label>Address Line 2</Label>
          <Input
            value={companyInfo.address_line_2}
            onChange={(e) => setCompanyInfo({ ...companyInfo, address_line_2: e.target.value })}
            placeholder="Suite, Floor, Building (optional)"
          />
        </div>
        <div>
          <Label>Country <span className="text-red-500">*</span></Label>
          <SearchableSelect
            options={countryList}
            value={companyInfo.country || ''}
            onChange={() => {}}
            placeholder="Country of registration"
            allowOther={false}
            disabled
            error={!!fieldErrors.country}
          />
          <FieldError fieldKey="country" />
        </div>
        <div>
          <Label>State / Region</Label>
          <SearchableSelect
            options={stateList}
            value={stateSelectValue}
            onChange={handleStateChange}
            placeholder={countryIsoCode ? 'Search or select state' : 'Select country first'}
            allowOther={!!countryIsoCode}
            disabled={!countryIsoCode}
            error={!!fieldErrors.state_region}
          />
          {stateSelectValue === '__other__' && (
            <Input
              className="mt-2"
              value={companyInfo.state_region}
              onChange={(e) => setCompanyInfo({ ...companyInfo, state_region: e.target.value })}
              placeholder="Type state or region"
            />
          )}
          <FieldError fieldKey="state_region" />
        </div>
        <div>
          <Label>City <span className="text-red-500">*</span></Label>
          <SearchableSelect
            options={cityList}
            value={citySelectValue}
            onChange={handleCityChange}
            placeholder={countryIsoCode ? 'Search or select city' : 'Select country first'}
            allowOther={!!countryIsoCode}
            disabled={!countryIsoCode}
            error={!!fieldErrors.city}
          />
          {citySelectValue === '__other__' && (
            <Input
              className={`mt-2 ${fieldErrors.city ? 'border-red-500' : ''}`}
              value={companyInfo.city}
              onChange={(e) => setCompanyInfo({ ...companyInfo, city: e.target.value })}
              placeholder="Type city"
            />
          )}
          <FieldError fieldKey="city" />
        </div>
        <div>
          <Label>Postal Code <span className="text-red-500">*</span></Label>
          <Input
            value={companyInfo.postal_code}
            onChange={(e) => setCompanyInfo({ ...companyInfo, postal_code: e.target.value })}
            placeholder="Postal / ZIP Code"
            className={fieldErrors.postal_code ? 'border-red-500' : ''}
          />
          <FieldError fieldKey="postal_code" />
        </div>
      </CardContent>
    </Card>
  );
}

// Same Country → State → City searchable dropdowns for each branch (like Head Office Address)
function BranchAddressFields({ branch, index, updateBranch }) {
  const countryList = useCanonicalCountryOptions();
  const [isCustomState, setIsCustomState] = useState(false);
  const [isCustomCity, setIsCustomCity] = useState(false);

  const countryIsoCode = useMemo(() => {
    return getCountryIsoCode(branch.country);
  }, [branch.country]);

  const stateList = useMemo(() => {
    return getStateOptionsForCountry(branch.country);
  }, [branch.country]);

  const stateIsOther = !!(branch.state_region && !stateList.some((s) => s.value === branch.state_region));
  const stateSelectValue = (isCustomState || stateIsOther) ? '__other__' : (branch.state_region || '');

  const stateIsoCode = useMemo(() => {
    if (!countryIsoCode || !branch.state_region || stateIsOther) return null;
    return getStateIsoCode(branch.country, branch.state_region);
  }, [countryIsoCode, branch.country, branch.state_region, stateIsOther]);

  const cityList = useMemo(() => {
    if (!countryIsoCode) return [];
    return getCityOptionsForCountry(branch.country, (isCustomState || stateIsOther) ? null : branch.state_region);
  }, [countryIsoCode, branch.country, branch.state_region, isCustomState, stateIsOther]);

  const cityIsOther = !!(branch.city && !cityList.some((c) => c.value === branch.city));
  const citySelectValue = (isCustomCity || cityIsOther) ? '__other__' : (branch.city || '');

  useEffect(() => {
    setIsCustomState(stateIsOther);
  }, [stateIsOther]);

  useEffect(() => {
    setIsCustomCity(cityIsOther);
  }, [cityIsOther]);

  const handleCountryChange = (value) => {
    const phoneCode = getPhoneCodeForCountry(value || '');
    setIsCustomState(false);
    setIsCustomCity(false);
    updateBranch(index, 'country', value || '');
    updateBranch(index, 'state_region', '');
    updateBranch(index, 'city', '');
    updateBranch(index, 'contact_country_code', phoneCode);
  };

  const handleStateChange = (value) => {
    if (value === '__other__') {
      setIsCustomState(true);
      setIsCustomCity(false);
      updateBranch(index, 'state_region', '');
      updateBranch(index, 'city', '');
      return;
    }
    setIsCustomState(false);
    setIsCustomCity(false);
    updateBranch(index, 'state_region', value || '');
    updateBranch(index, 'city', '');
  };

  const handleCityChange = (value) => {
    if (value === '__other__') {
      setIsCustomCity(true);
      updateBranch(index, 'city', '');
      return;
    }
    setIsCustomCity(false);
    updateBranch(index, 'city', value || '');
  };

  return (
    <>
      <div>
        <Label>Country <span className="text-red-500">*</span></Label>
        <Input
          value={branch.country || ''}
          disabled
          className="bg-gray-100 text-gray-700 cursor-not-allowed"
        />
        <p className="text-xs text-gray-500 mt-1">Fixed to registration country</p>
      </div>
      <div>
        <Label>State / Region</Label>
        <SearchableSelect
          options={stateList}
          value={stateSelectValue}
          onChange={handleStateChange}
          placeholder={countryIsoCode ? 'Search or select state' : 'Select country first'}
          allowOther={!!countryIsoCode}
          disabled={!countryIsoCode}
        />
        {stateSelectValue === '__other__' && (
          <Input
            className="mt-2"
            value={branch.state_region}
            onChange={(e) => updateBranch(index, 'state_region', e.target.value)}
            placeholder="Type state or region"
          />
        )}
      </div>
      <div>
        <Label>City <span className="text-red-500">*</span></Label>
        <SearchableSelect
          options={cityList}
          value={citySelectValue}
          onChange={handleCityChange}
          placeholder={countryIsoCode ? 'Search or select city' : 'Select country first'}
          allowOther={!!countryIsoCode}
          disabled={!countryIsoCode}
        />
        {citySelectValue === '__other__' && (
          <Input
            className="mt-2"
            value={branch.city}
            onChange={(e) => updateBranch(index, 'city', e.target.value)}
            placeholder="Type city"
          />
        )}
      </div>
    </>
  );
}

const CompanyRegistration = ({
  adminMode = false,
  adminMemberId = null,
  adminApi = null,
  adminPrefillData = null,
  adminRegistrationData = null,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, member, refreshUser } = useAuth();
  const apiClient = adminMode && adminApi ? adminApi : api;
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [draftSaveStatus, setDraftSaveStatus] = useState('idle'); // idle | saving | saved | error
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({}); // Field-level validation errors
  const [validatingNext, setValidatingNext] = useState(false);
  const draftRestoringRef = useRef(false);
  const autosaveStartedRef = useRef(false);
  const autosaveDebounceRef = useRef(null);
  const autosaveDirtyRef = useRef(false);
  const autosaveInFlightRef = useRef(false);
  const isCompanyMember = !adminMode && (
    member?.has_admin_rights === false ||
    member?.role === 'Company Member' ||
    Boolean(member?.invited_by)
  );

  // Get pre-filled data from navigation state
  const prefillData = adminPrefillData || location.state?.prefillData || {};

  // Get email from various sources
  const getUserEmail = () => {
    return prefillData.primary_contact_email || member?.email || user?.email || '';
  };

  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const langDropdownRef = useRef(null);
  const [strengthsDropdownOpen, setStrengthsDropdownOpen] = useState(false);
  const strengthsDropdownRef = useRef(null);
  const [strengthsSearch, setStrengthsSearch] = useState('');

  // Close multi-select dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
        setLangDropdownOpen(false);
      }
      if (strengthsDropdownRef.current && !strengthsDropdownRef.current.contains(e.target)) {
        setStrengthsDropdownOpen(false);
        setStrengthsSearch('');
      }
    };
    if (langDropdownOpen || strengthsDropdownOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [langDropdownOpen, strengthsDropdownOpen]);

  // Step 1: Company Information
  const [companyInfo, setCompanyInfo] = useState({
    company_name: prefillData.company_name || '',
    business_registration_number: '',
    trading_name: '',
    year_established: '',
    linkedin_page: '',
    country_of_registration: prefillData.country_of_registration || prefillData.country || member?.country || user?.country || '',
    company_website: '',
    // Head Office Address
    street_address: '',
    address_line_2: '',
    city: prefillData.city || member?.city || user?.city || '',
    state_region: '',
    country: prefillData.country || member?.country || user?.country || '',
    postal_code: '',
    // Primary Contact
    primary_contact_name: prefillData.primary_contact_name || member?.individual_name || user?.full_name || '',
    primary_contact_designation: '',
    primary_contact_email: prefillData.primary_contact_email || member?.email || user?.email || '',
    primary_contact_country_code: '+1',
    primary_contact_mobile: prefillData.primary_contact_mobile || '',
    // Secondary Contact
    secondary_contact_name: '',
    secondary_contact_designation: '',
    secondary_contact_email: '',
    secondary_contact_country_code: '+1',
    secondary_contact_mobile: '',
    // Company Profile
    total_employees: '',
    languages_spoken: [],
    operating_hours: '',
    key_ports_airports: '',
    service_capabilities: '',
    company_strengths: [],
    main_trade_lanes: '',
    markets_to_develop: '',
    // Files
    company_logo: null,
    company_brochure: null,
  });
  const selectedTradeLaneValues = useMemo(() => (
    String(companyInfo.main_trade_lanes || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean)
  ), [companyInfo.main_trade_lanes]);
  const tradeLaneOptionsForDropdown = useMemo(() => {
    const additionalOptions = selectedTradeLaneValues
      .filter((value) => !TRADE_LANE_OPTIONS.some((option) => option.value === value))
      .map((value) => ({ value, label: value }));
    return [...TRADE_LANE_OPTIONS, ...additionalOptions];
  }, [selectedTradeLaneValues]);

  // Step 2: Branch Offices
  const [branches, setBranches] = useState([]);

  // Step 3: Financial Information
  const [financialInfo, setFinancialInfo] = useState({
    years_in_operation: '',
    last_fiscal_turnover: '',
    turnover_currency: 'USD',
    existing_memberships: '',
    history_of_disputes: 'no',
    dispute_reasons: '',
    // Bank Information
    bank_name: '',
    bank_branch_address: '',
    account_number: '',
    swift_iban: '',
    account_holder_name: '',
  });

  // Trade References
  const [tradeReferences, setTradeReferences] = useState([
    createEmptyTradeReference(),
    createEmptyTradeReference()
  ]);

  // Step 4: Compliance & Verification
  const [complianceInfo, setComplianceInfo] = useState({
    lei_number: '',
    tax_registration_number: '',
    // Documents
    financial_statement: null,
    registration_certificate: null,
    iata_fiata_iso: null,
    business_license: null,
    dg_certification: null,
    insurance_certificate: null,
    // Declarations
    info_accuracy_confirmed: false,
    no_fraud_confirmed: false,
    approval_process_acknowledged: false,
    network_rules_agreed: false,
    linkedin_onboarding_consent: false,
    // Applicant
    applicant_name: '',
    applicant_designation: '',
  });


  const countryList = useCanonicalCountryOptions();

  const phoneCodeOptions = useMemo(() => {
    const seen = new Set();
    return Country.getAllCountries()
      .filter((c) => c.phonecode)
      .map((c) => {
        const code = normalizePhoneCode(c.phonecode);
        if (seen.has(code)) return null;
        seen.add(code);
        return { code, label: code };
      })
      .filter(Boolean);
  }, []);

  const steps = [
    { number: 1, title: 'Company Information', icon: Building2, desc: 'Basic details, address & profile' },
    { number: 2, title: 'Branch Offices', icon: MapPin, desc: 'Additional locations (optional)' },
    { number: 3, title: 'Financial Information', icon: DollarSign, desc: 'Financials & trade references' },
    { number: 4, title: 'Compliance & Verification', icon: Shield, desc: 'Documents & declarations' },
  ];

  const handleFileUpload = async (file, fieldName, setter, currentState, onSuccess) => {
    if (!file) return;
    if (isCompanyMember) {
      toast.error('Company registration documents can only be managed by the company admin.');
      return;
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);
    // Map field names to upload types
    const uploadTypeMap = {
      'company_logo': 'logo',
      'company_brochure': 'brochure'
    };
    formData.append('upload_type', uploadTypeMap[fieldName] || 'registration_document');

    try {
      const response = await apiClient.post('/api/uploads/file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Store both file_url and original_filename
      setter({
        ...currentState,
        [fieldName]: {
          file_url: response.data.file_url,
          original_filename: response.data.original_filename || file.name
        }
      });
      toast.success('File uploaded successfully');
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const addBranch = () => {
    const defaultCountry = companyInfo.country || '';
    const defaultPhoneCode = getPhoneCodeForCountry(defaultCountry);

    setBranches([...branches, {
      branch_name: '',
      branch_type: '',
      street_address: '',
      state_region: '',
      city: '',
      country: defaultCountry,
      contact_name: '',
      contact_designation: '',
      contact_email: '',
      contact_country_code: defaultPhoneCode,
      contact_mobile: ''
    }]);
  };

  const removeBranch = (index) => {
    setBranches(branches.filter((_, i) => i !== index));
  };

  const updateBranch = (index, field, value) => {
    setBranches((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });

    if (field === 'contact_email') {
      const branchEmail = String(value || '').trim();
      const registeredDomain = getEmailDomain(companyInfo.primary_contact_email || getUserEmail());
      const branchDomain = getEmailDomain(branchEmail);
      const errorKey = `branch_${index}_contact_email`;

      setFieldErrors((prev) => {
        const next = { ...prev };

        if (!branchEmail || !registeredDomain || branchDomain === registeredDomain) {
          delete next[errorKey];
        } else {
          next[errorKey] = `Branch email must use the registered domain (${registeredDomain})`;
        }

        return next;
      });
    }
  };

  useEffect(() => {
    const registeredDomain = getEmailDomain(companyInfo.primary_contact_email || getUserEmail());

    setFieldErrors((prev) => {
      const next = { ...prev };

      branches.forEach((branch, index) => {
        const errorKey = `branch_${index}_contact_email`;
        const branchEmail = String(branch.contact_email || '').trim();
        const branchDomain = getEmailDomain(branchEmail);

        if (!branchEmail || !registeredDomain || branchDomain === registeredDomain) {
          delete next[errorKey];
        } else {
          next[errorKey] = `Branch email must use the registered domain (${registeredDomain})`;
        }
      });

      return next;
    });
  }, [branches, companyInfo.primary_contact_email, member?.email, prefillData.primary_contact_email, user?.email]);

  const addTradeReference = () => {
    setTradeReferences([...tradeReferences, createEmptyTradeReference()]);
  };

  const removeTradeReference = (index) => {
    if (tradeReferences.length > MIN_INDUSTRY_REFERENCES) {
      setTradeReferences(tradeReferences.filter((_, i) => i !== index));
    }
  };

  const updateTradeReference = (index, field, value) => {
    const updated = [...tradeReferences];
    updated[index][field] = value;
    setTradeReferences(updated);
  };

  const validateStep = (step) => {
    const errors = {};
    switch (step) {
      case 1: {
        const hasCompanyLogo = companyInfo.company_logo && (
          typeof companyInfo.company_logo === 'object' ? companyInfo.company_logo.file_url : companyInfo.company_logo
        );
        if (!companyInfo.company_name) errors.company_name = 'Company name is required';
        if (!companyInfo.business_registration_number) errors.business_registration_number = 'Business registration / license number is required';
        if (!companyInfo.year_established) errors.year_established = 'Year of establishment is required';
        if (!companyInfo.country_of_registration) errors.country_of_registration = 'Country of registration is required';
        if (!companyInfo.company_website) errors.company_website = 'Company website is required';
        if (!companyInfo.street_address) errors.street_address = 'Street address is required';
        if (!companyInfo.city) errors.city = 'City is required';
        if (!companyInfo.country) errors.country = 'Country is required';
        if (!companyInfo.postal_code) errors.postal_code = 'Postal code is required';
        if (!companyInfo.primary_contact_name) errors.primary_contact_name = 'Primary contact name is required';
        if (!companyInfo.primary_contact_designation) errors.primary_contact_designation = 'Designation is required';
        if (!companyInfo.primary_contact_email) errors.primary_contact_email = 'Primary contact email is required';
        if (!companyInfo.primary_contact_mobile) errors.primary_contact_mobile = 'Primary contact phone is required';
        if (!companyInfo.total_employees) errors.total_employees = 'Total employees is required';
        if (!companyInfo.languages_spoken || (Array.isArray(companyInfo.languages_spoken) && companyInfo.languages_spoken.length === 0)) errors.languages_spoken = 'Languages spoken is required';
        if (!companyInfo.key_ports_airports) errors.key_ports_airports = 'Key airports/ports is required';
        if (!companyInfo.service_capabilities) errors.service_capabilities = 'Service capabilities is required';
        if (!companyInfo.company_strengths || (Array.isArray(companyInfo.company_strengths) && companyInfo.company_strengths.length === 0)) errors.company_strengths = 'Company strengths / specialization is required';
        if (!companyInfo.main_trade_lanes) errors.main_trade_lanes = 'Main trade lanes is required';
        if (!companyInfo.markets_to_develop) errors.markets_to_develop = 'Markets to develop is required';
        if (!hasCompanyLogo) errors.company_logo = 'Company logo is required';
        break;
      }
      case 2:
        if (branches.length > 0) {
          const registeredDomain = getEmailDomain(companyInfo.primary_contact_email || getUserEmail());
          branches.forEach((branch, index) => {
            const branchEmail = String(branch.contact_email || '').trim();
            const branchDomain = getEmailDomain(branchEmail);
            if (branchEmail && registeredDomain && branchDomain !== registeredDomain) {
              errors[`branch_${index}_contact_email`] = `Branch email must use the registered domain (${registeredDomain})`;
            }
          });
        }
        break;
      case 3: {
        if (!financialInfo.years_in_operation) errors.years_in_operation = 'Years in operation is required';
        if (!financialInfo.last_fiscal_turnover) errors.last_fiscal_turnover = 'Last fiscal turnover is required';
        if (!financialInfo.bank_name) errors.bank_name = 'Bank name is required';
        if (!financialInfo.account_number) errors.account_number = 'Bank account number is required';
        if (!financialInfo.swift_iban) errors.swift_iban = 'SWIFT/IBAN is required';
        if (!financialInfo.account_holder_name) errors.account_holder_name = 'Account holder name is required';
        if (tradeReferences.length < MIN_INDUSTRY_REFERENCES) {
          errors.trade_references_min = 'Minimum 2 Industry References are required.';
        }
        for (let i = 0; i < Math.min(MIN_INDUSTRY_REFERENCES, tradeReferences.length); i += 1) {
          if (!tradeReferences[i]?.company_name) errors[`trade_ref_${i}_company_name`] = 'Industry reference company name is required';
          if (!tradeReferences[i]?.contact_person) errors[`trade_ref_${i}_contact_person`] = 'Industry reference contact person is required';
          if (!tradeReferences[i]?.email) errors[`trade_ref_${i}_email`] = 'Industry reference email is required';
          if (!tradeReferences[i]?.mobile) errors[`trade_ref_${i}_mobile`] = 'Industry reference mobile is required';
          if (!tradeReferences[i]?.relationship_duration) errors[`trade_ref_${i}_relationship_duration`] = 'Industry reference relationship duration is required';
        }
        break;
      }
      case 4: {
        if (!complianceInfo.tax_registration_number) errors.tax_registration_number = 'Tax registration number is required';
        if (!complianceInfo.registration_certificate) errors.registration_certificate = 'Registration certificate is required';
        if (!complianceInfo.financial_statement) errors.financial_statement = 'Financial statement is required';
        if (!complianceInfo.info_accuracy_confirmed) errors.info_accuracy_confirmed = 'Please accept this declaration';
        if (!complianceInfo.no_fraud_confirmed) errors.no_fraud_confirmed = 'Please accept this declaration';
        if (!complianceInfo.approval_process_acknowledged) errors.approval_process_acknowledged = 'Please accept this declaration';
        if (!complianceInfo.network_rules_agreed) errors.network_rules_agreed = 'Please accept this declaration';
        if (!complianceInfo.applicant_name) errors.applicant_name = 'Applicant name is required';
        if (!complianceInfo.applicant_designation) errors.applicant_designation = 'Designation is required';
        break;
      }
      default:
        return true;
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      toast.error(errors.trade_references_min || 'Please fill all required fields');
      return false;
    }
    setFieldErrors({});
    return true;
  };

  const validateStepServer = async (step) => {
    const errors = {};

    if (step === 1) {
      const email = String(companyInfo.primary_contact_email || '').trim().toLowerCase();
      if (email) {
        try {
          await api.get(`${API_URL}/api/company-registration/validate-email/${encodeURIComponent(email)}`);
        } catch (error) {
          const detail = String(error?.response?.data?.detail || '').toLowerCase();
          if (detail.includes('already registered')) {
            errors.primary_contact_email = 'This email is already registered. Please use a different email address.';
          } else if (detail) {
            errors.primary_contact_email = error.response?.data?.detail;
          } else if (!error?.response) {
            console.warn('Email availability validation skipped because the server did not respond.', error);
          }
        }
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors((prev) => ({ ...prev, ...errors }));
      return false;
    }
    return true;
  };

  const handleNext = async () => {
    if (validatingNext) return;
    if (!validateStep(currentStep)) return;

    setValidatingNext(true);
    try {
      const serverValid = await validateStepServer(currentStep);
      if (!serverValid) return;
      autosaveDirtyRef.current = true;
      await saveDraft({ silent: true, overrideStep: currentStep + 1 });
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    } finally {
      setValidatingNext(false);
    }
  };

  const handleBack = () => {
    setFieldErrors({});
    setCurrentStep(currentStep - 1);
    window.scrollTo(0, 0);
  };

  const buildDraftPayload = (overrideStep = null) => {
    const email = companyInfo.primary_contact_email || getUserEmail();
    if (!email) return null;

    const preparedCompanyInfo = {
      ...companyInfo,
      main_trade_lanes: Array.isArray(companyInfo.main_trade_lanes)
        ? companyInfo.main_trade_lanes.map((item) => String(item || '').trim()).filter(Boolean).join(', ')
        : String(companyInfo.main_trade_lanes || '')
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean)
          .join(', '),
      company_logo: companyInfo.company_logo || null,
      company_brochure: companyInfo.company_brochure || null
    };

    return {
      company_info: preparedCompanyInfo,
      branches: branches,
      financial_info: financialInfo,
      trade_references: tradeReferences,
      compliance_info: complianceInfo,
      email: email,
      current_step: overrideStep ?? currentStep
    };
  };

  const saveDraft = async ({ silent = true, overrideStep = null } = {}) => {
    if (adminMode) return;
    if (isCompanyMember) {
      if (!silent) {
        toast.error('Company registration documents can only be managed by the company admin.');
      }
      return;
    }

    const draftData = buildDraftPayload(overrideStep);
    if (!draftData) {
      if (!silent) {
        toast.error('Please enter primary contact email to save draft');
      }
      return;
    }

    if (autosaveInFlightRef.current) return;
    autosaveInFlightRef.current = true;
    setSavingDraft(true);
    setDraftSaveStatus('saving');
    try {
      await api.post(`${API_URL}/api/company-registration/save-draft`, draftData);

      if (refreshUser) await refreshUser();
      autosaveDirtyRef.current = false;
      setLastSavedAt(new Date());
      setDraftSaveStatus('saved');
      if (!silent) {
        toast.success('Draft saved successfully! You can continue later.');
      }
    } catch (error) {
      console.error('Save draft error:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to save draft. Please try again.';
      setDraftSaveStatus('error');
      if (!silent) {
        toast.error(errorMessage);
      }
    } finally {
      setSavingDraft(false);
      autosaveInFlightRef.current = false;
    }
  };

  const handleSaveDraft = async () => {
    await saveDraft({ silent: false });
  };

  // Load draft on component mount
  useEffect(() => {
    if (adminMode) return;
    const loadDraft = async () => {
      const email = getUserEmail();
      if (!email || draftLoaded) return;

      try {
        draftRestoringRef.current = true;
        const response = await api.get(`${API_URL}/api/company-registration/draft/${email}`);
        if (response.data.exists) {
          const draft = response.data;

          // Load company info (merge with prefill data)
          if (draft.company_info) {
            // Helper function to convert string URLs to file objects
            const convertFileField = (fileValue) => {
              if (!fileValue) return null;
              // If it's already an object, return as-is
              if (typeof fileValue === 'object') return fileValue;
              // If it's a string URL, convert to object format
              if (typeof fileValue === 'string') {
                const filename = fileValue.split('/').pop();
                return {
                  file_url: fileValue,
                  original_filename: filename
                };
              }
              return null;
            };

            // Normalize array fields from draft (may be comma-separated strings)
            const normArray = (val) => Array.isArray(val)
              ? val
              : (typeof val === 'string' && val.trim() ? val.split(',').map(s => s.trim()).filter(Boolean) : []);
            const normalizedLangs = normArray(draft.company_info.languages_spoken);
            const normalizedStrengths = normArray(draft.company_info.company_strengths);

            setCompanyInfo(prev => ({
              ...prev,
              ...draft.company_info,
              // Preserve prefill data if draft doesn't have it
              company_name: draft.company_info.company_name || prev.company_name,
              primary_contact_email: draft.company_info.primary_contact_email || prev.primary_contact_email,
              // Convert file fields to proper object format
              company_logo: convertFileField(draft.company_info.company_logo) || prev.company_logo,
              company_brochure: convertFileField(draft.company_info.company_brochure) || prev.company_brochure,
              languages_spoken: normalizedLangs,
              company_strengths: normalizedStrengths,
            }));
          }

          // Load branches
          if (draft.branches && draft.branches.length > 0) {
            setBranches(draft.branches);
          }

          // Load financial info
          if (draft.financial_info) {
            setFinancialInfo(prev => ({ ...prev, ...draft.financial_info }));
          }

          // Load trade references
          if (draft.trade_references && draft.trade_references.length > 0) {
            setTradeReferences(draft.trade_references);
          }

          // Load compliance info
          if (draft.compliance_info) {
            // Helper function to convert string URLs to file objects for compliance documents
            const convertComplianceFile = (fileValue) => {
              if (!fileValue) return null;
              if (typeof fileValue === 'object') return fileValue;
              if (typeof fileValue === 'string') {
                const filename = fileValue.split('/').pop();
                return {
                  file_url: fileValue,
                  original_filename: filename
                };
              }
              return null;
            };

            const complianceData = { ...draft.compliance_info };
            // Convert all file fields in compliance_info
            const fileFields = ['financial_statement', 'registration_certificate', 'iata_fiata_iso',
              'business_license', 'dg_certification', 'insurance_certificate'];
            fileFields.forEach(field => {
              if (complianceData[field]) {
                complianceData[field] = convertComplianceFile(complianceData[field]);
              }
            });

            setComplianceInfo(prev => ({ ...prev, ...complianceData }));
          }

          // Set current step
          if (draft.current_step) {
            setCurrentStep(draft.current_step);
          }

          if (draft.updated_at) {
            const savedTime = new Date(draft.updated_at);
            if (!Number.isNaN(savedTime.getTime())) {
              setLastSavedAt(savedTime);
            }
          }
          setDraftSaveStatus('saved');
          toast.success('Draft loaded successfully!');
          setDraftLoaded(true);
        }
      } catch (error) {
        console.error('Load draft error:', error);
        // Don't show error if no draft exists
        if (error.response?.status !== 404) {
          toast.error('Failed to load draft');
        }
      } finally {
        draftRestoringRef.current = false;
      }
    };

    loadDraft();
  }, [draftLoaded, adminMode]);

  useEffect(() => {
    if (!adminMode || !adminRegistrationData) return;

    const convertFileField = (fileValue) => {
      if (!fileValue) return null;
      if (typeof fileValue === 'object') return fileValue;
      if (typeof fileValue === 'string') {
        return {
          file_url: fileValue,
          original_filename: fileValue.split('/').pop()
        };
      }
      return null;
    };
    const normArray = (val) => Array.isArray(val)
      ? val
      : (typeof val === 'string' && val.trim() ? val.split(',').map(s => s.trim()).filter(Boolean) : []);

    if (adminRegistrationData.company_info) {
      setCompanyInfo(prev => ({
        ...prev,
        ...adminRegistrationData.company_info,
        languages_spoken: normArray(adminRegistrationData.company_info.languages_spoken),
        company_strengths: normArray(adminRegistrationData.company_info.company_strengths),
        company_logo: convertFileField(adminRegistrationData.company_info.company_logo),
        company_brochure: convertFileField(adminRegistrationData.company_info.company_brochure),
      }));
    }
    if (Array.isArray(adminRegistrationData.branches)) {
      setBranches(adminRegistrationData.branches);
    }
    if (adminRegistrationData.financial_info) {
      setFinancialInfo(prev => ({ ...prev, ...adminRegistrationData.financial_info }));
    }
    if (Array.isArray(adminRegistrationData.trade_references) && adminRegistrationData.trade_references.length > 0) {
      setTradeReferences(adminRegistrationData.trade_references);
    }
    if (adminRegistrationData.compliance_info) {
      const complianceData = { ...adminRegistrationData.compliance_info };
      ['financial_statement', 'registration_certificate', 'iata_fiata_iso', 'business_license', 'dg_certification', 'insurance_certificate'].forEach((field) => {
        complianceData[field] = convertFileField(complianceData[field]);
      });
      setComplianceInfo(prev => ({ ...prev, ...complianceData }));
    }
  }, [adminMode, adminRegistrationData]);

  useEffect(() => {
    if (adminMode) return;
    if (draftRestoringRef.current) return;
    if (!autosaveStartedRef.current) {
      autosaveStartedRef.current = true;
      return;
    }

    const email = companyInfo.primary_contact_email || getUserEmail();
    if (!email) return;

    autosaveDirtyRef.current = true;
    if (draftSaveStatus !== 'saving') {
      setDraftSaveStatus('idle');
    }

    if (autosaveDebounceRef.current) {
      clearTimeout(autosaveDebounceRef.current);
    }
    autosaveDebounceRef.current = setTimeout(() => {
      if (!autosaveDirtyRef.current || autosaveInFlightRef.current) return;
      saveDraft({ silent: true });
    }, 3000);
  }, [companyInfo, branches, financialInfo, tradeReferences, complianceInfo, currentStep, adminMode]);

  useEffect(() => {
    if (adminMode) return undefined;
    const intervalId = setInterval(() => {
      if (autosaveDirtyRef.current && !autosaveInFlightRef.current) {
        saveDraft({ silent: true });
      }
    }, 15000);
    return () => clearInterval(intervalId);
  }, [companyInfo, branches, financialInfo, tradeReferences, complianceInfo, currentStep, adminMode]);

  useEffect(() => () => {
    if (autosaveDebounceRef.current) {
      clearTimeout(autosaveDebounceRef.current);
    }
  }, []);

  useEffect(() => {
    if (!companyInfo.country) return;

    const defaultPhoneCode = getPhoneCodeForCountry(companyInfo.country);
    setBranches((prev) => {
      let hasChanges = false;
      const next = prev.map((branch) => {
        if (branch.country) return branch;
        hasChanges = true;
        return {
          ...branch,
          country: companyInfo.country,
          contact_country_code: branch.contact_country_code || defaultPhoneCode,
        };
      });
      return hasChanges ? next : prev;
    });
  }, [companyInfo.country]);

  const handleSubmit = async () => {
    if (isCompanyMember) {
      toast.error('Company registration documents can only be managed by the company admin.');
      return;
    }

    if (!validateStep(4)) return;

    setLoading(true);
    try {
      // Prepare company_info with file URLs extracted from objects
      const preparedCompanyInfo = {
        ...companyInfo,
        languages_spoken: Array.isArray(companyInfo.languages_spoken)
          ? companyInfo.languages_spoken.join(', ')
          : companyInfo.languages_spoken,
        company_strengths: Array.isArray(companyInfo.company_strengths)
          ? companyInfo.company_strengths.join(', ')
          : companyInfo.company_strengths,
        main_trade_lanes: Array.isArray(companyInfo.main_trade_lanes)
          ? companyInfo.main_trade_lanes.map((item) => String(item || '').trim()).filter(Boolean).join(', ')
          : String(companyInfo.main_trade_lanes || '')
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean)
            .join(', '),
        company_logo: companyInfo.company_logo
          ? (typeof companyInfo.company_logo === 'object'
            ? companyInfo.company_logo.file_url
            : companyInfo.company_logo)
          : null,
        company_brochure: companyInfo.company_brochure
          ? (typeof companyInfo.company_brochure === 'object'
            ? companyInfo.company_brochure.file_url
            : companyInfo.company_brochure)
          : null
      };

      // Prepare compliance_info with file URLs extracted from objects
      const fileFields = ['financial_statement', 'registration_certificate', 'iata_fiata_iso',
        'business_license', 'dg_certification', 'insurance_certificate'];
      const preparedComplianceInfo = {
        ...complianceInfo,
        application_date: new Date().toISOString()
      };
      fileFields.forEach(field => {
        if (preparedComplianceInfo[field]) {
          preparedComplianceInfo[field] = typeof preparedComplianceInfo[field] === 'object'
            ? preparedComplianceInfo[field].file_url
            : preparedComplianceInfo[field];
        }
      });

      const registrationData = {
        // Step 1
        company_info: preparedCompanyInfo,
        // Step 2
        branches: branches,
        // Step 3
        financial_info: financialInfo,
        trade_references: tradeReferences,
        // Step 4
        compliance_info: preparedComplianceInfo,
        // Account
        email: companyInfo.primary_contact_email,
        // Metadata
        status: 'pending',
        submitted_at: new Date().toISOString()
      };

      const response = await apiClient.post(
        adminMode
          ? `/api/admin/company-registrations/bolt-members/${adminMemberId}/complete-onboarding`
          : '/api/company-registration/submit',
        registrationData
      );

      // Refresh auth context so sidebar/header show the new company logo immediately
      if (!adminMode && refreshUser) await refreshUser();

      toast.success(adminMode ? 'Bolt Member onboarding completed successfully.' : 'Registration submitted successfully! You will be notified once approved.');
      navigate(adminMode ? '/admin/members' : '/registration-success');
    } catch (error) {
      console.error('Registration error:', error);
      const detail = String(error?.response?.data?.detail || '').toLowerCase();
      if (detail.includes('already registered')) {
        setCurrentStep(1);
        setFieldErrors((prev) => ({
          ...prev,
          primary_contact_email: 'This email is already registered. Please use a different email address.'
        }));
        window.scrollTo(0, 0);
        return;
      }
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Registration failed. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFile = async (fileValue, fieldName, setter, currentState) => {
    if (!fileValue) return;
    if (isCompanyMember) {
      toast.error('Company registration documents can only be managed by the company admin.');
      return;
    }

    try {
      // Extract file URL and category
      const fileUrl = typeof fileValue === 'object' ? fileValue.file_url : fileValue;
      if (!fileUrl) {
        // Just clear the state if no URL
        setter({ ...currentState, [fieldName]: null });
        return;
      }

      // Parse the URL to get category and filename
      // URL format: /api/uploads/files/{category}/{filename}
      const urlParts = fileUrl.split('/');
      const categoryIndex = urlParts.indexOf('files');
      if (categoryIndex !== -1 && categoryIndex + 2 < urlParts.length) {
        const category = urlParts[categoryIndex + 1];
        const filename = urlParts[categoryIndex + 2];

        // Call delete API
        await apiClient.delete(`/api/uploads/files/${category}/${filename}`);
        toast.success('File deleted successfully');
      }

      // Clear the state
      setter({ ...currentState, [fieldName]: null });
    } catch (error) {
      console.error('Delete file error:', error);
      // Even if API call fails, clear the state
      setter({ ...currentState, [fieldName]: null });
      toast.error('Failed to delete file from server, but removed from form');
    }
  };

  const FieldError = ({ fieldKey }) =>
    fieldErrors[fieldKey] ? (
      <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
        <AlertCircle size={14} />
        {fieldErrors[fieldKey]}
      </p>
    ) : null;

  const clearFieldError = (fieldKey) => {
    setFieldErrors(prev => {
      const next = { ...prev };
      delete next[fieldKey];
      return next;
    });
  };

  const FileUploadField = ({ label, required, fieldName, currentValue, onUpload, accept = ".pdf,.jpg,.jpeg,.png", setter, currentState }) => {
    // Handle both string (legacy) and object formats
    const displayName = currentValue
      ? (typeof currentValue === 'object' && currentValue.original_filename
        ? currentValue.original_filename
        : typeof currentValue === 'string'
          ? currentValue.split('/').pop()
          : '')
      : null;

    return (
      <div>
        <Label className="flex items-center">
          {label} {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
        <div className="mt-1">
          {currentValue ? (
            <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle size={18} className="text-green-600" />
              <span className="text-sm text-green-700 flex-1 truncate">{displayName}</span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleDeleteFile(currentValue, fieldName, setter, currentState)}
              >
                <Trash2 size={14} className="text-red-500" />
              </Button>
            </div>
          ) : (
            <label className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
              <input
                type="file"
                accept={accept}
                className="hidden"
                onChange={(e) => onUpload(e.target.files[0])}
                disabled={uploadingFile}
              />
              {uploadingFile ? (
                <Loader2 size={20} className="animate-spin text-blue-600" />
              ) : (
                <>
                  <Upload size={20} className="text-gray-400 mr-2" />
                  <span className="text-sm text-gray-500">Click to upload</span>
                </>
              )}
            </label>
          )}
        </div>
      </div>
    );
  };

  if (isCompanyMember) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="bg-gradient-to-r from-blue-900 to-indigo-900 text-white py-8">
          <div className="max-w-3xl mx-auto px-4 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Company Registration</h1>
              <p className="text-blue-200">Managed by your company admin</p>
            </div>
            <Button
              variant="outline"
              className="bg-white/10 hover:bg-white/20 text-white border-white/30"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft size={16} className="mr-2" />
              Dashboard
            </Button>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 py-10">
          <Card>
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <Shield className="text-blue-600 mt-1" size={24} />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">No document upload needed</h2>
                  <p className="text-gray-600 mt-2">
                    Your company registration documents are controlled by the first registered company user. You can use the platform directly with your team access.
                  </p>
                  <Button className="mt-6" onClick={() => navigate('/dashboard')}>
                    Go to Dashboard
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-indigo-900 text-white py-8">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                <img
                  src={LOGO_URL}
                  alt="One Globe Alliance"
                  className="w-10 h-10 object-contain"
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold">One Globe Alliance</h1>
                <p className="text-blue-200">Company Membership Registration</p>
              </div>
            </div>
            {isAuthenticated && (
              <Link to="/dashboard">
                <Button
                  variant="outline"
                  className="bg-white/10 hover:bg-white/20 text-white border-white/30"
                >
                  <ArrowLeft size={16} className="mr-2" />
                  Back to Dashboard
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.number;
              const isCompleted = currentStep > step.number;

              return (
                <React.Fragment key={step.number}>
                  <div className={`flex items-center ${isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-400'}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${isActive ? 'border-blue-600 bg-blue-50' :
                      isCompleted ? 'border-green-600 bg-green-50' :
                        'border-gray-300'
                      }`}>
                      {isCompleted ? <Check size={18} /> : <Icon size={18} />}
                    </div>
                    <div className="ml-3 hidden md:block">
                      <p className={`text-sm font-medium ${isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-500'}`}>
                        Step {step.number}
                      </p>
                      <p className="text-xs text-gray-500">{step.title}</p>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-1 mx-4 rounded ${isCompleted ? 'bg-green-500' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Step 1: Company Information */}
        {currentStep === 1 && (
          <div className="space-y-6">
            {/* Company Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Building2 size={20} className="mr-2 text-blue-600" />
                  Company Details
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Company Name <span className="text-red-500">*</span></Label>
                  <Input
                    value={companyInfo.company_name}
                    onChange={(e) => { setCompanyInfo({ ...companyInfo, company_name: e.target.value }); clearFieldError('company_name'); }}
                    placeholder="Enter legal company name"
                    className={fieldErrors.company_name ? 'border-red-500' : ''}
                  />
                  <FieldError fieldKey="company_name" />
                </div>
                <div>
                  <Label>Business Registration / License Number <span className="text-red-500">*</span></Label>
                  <Input
                    value={companyInfo.business_registration_number}
                    onChange={(e) => { setCompanyInfo({ ...companyInfo, business_registration_number: e.target.value }); clearFieldError('business_registration_number'); }}
                    placeholder="e.g., 12345678"
                    className={fieldErrors.business_registration_number ? 'border-red-500' : ''}
                  />
                  <FieldError fieldKey="business_registration_number" />
                </div>
                <div>
                  <Label>Trading Name</Label>
                  <Input
                    value={companyInfo.trading_name}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, trading_name: e.target.value })}
                    placeholder="If different from company name"
                  />
                </div>
                <div>
                  <Label>Year of Establishment <span className="text-red-500">*</span></Label>
                  <Input
                    type="number"
                    min="1900"
                    max={new Date().getFullYear()}
                    value={companyInfo.year_established}
                    onChange={(e) => { setCompanyInfo({ ...companyInfo, year_established: e.target.value }); clearFieldError('year_established'); }}
                    placeholder="e.g., 2010"
                    className={fieldErrors.year_established ? 'border-red-500' : ''}
                  />
                  <FieldError fieldKey="year_established" />
                </div>
                <div>
                  <Label>Country of Registration <span className="text-red-500">*</span></Label>
                  <SearchableSelect
                    options={countryList}
                    value={companyInfo.country_of_registration || ''}
                    onChange={(value) => {
                      const nextCountry = value || '';
                      const code = getPhoneCodeForCountry(nextCountry);
                      setCompanyInfo({
                        ...companyInfo,
                        country_of_registration: nextCountry,
                        country: nextCountry,
                        state_region: '',
                        city: '',
                        primary_contact_country_code: code,
                        secondary_contact_country_code: code,
                      });
                      clearFieldError('country_of_registration');
                      clearFieldError('country');
                      clearFieldError('state_region');
                      clearFieldError('city');
                    }}
                    placeholder="Search or select country"
                    allowOther={false}
                    error={!!fieldErrors.country_of_registration}
                  />
                  <FieldError fieldKey="country_of_registration" />
                </div>
                <div>
                  <Label>Company Website <span className="text-red-500">*</span></Label>
                  <Input
                    type="url"
                    value={companyInfo.company_website}
                    onChange={(e) => { setCompanyInfo({ ...companyInfo, company_website: e.target.value }); clearFieldError('company_website'); }}
                    placeholder="https://www.example.com"
                    className={fieldErrors.company_website ? 'border-red-500' : ''}
                  />
                  <FieldError fieldKey="company_website" />
                </div>
                <div>
                  <Label>LinkedIn Page</Label>
                  <Input
                    type="url"
                    value={companyInfo.linkedin_page}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, linkedin_page: e.target.value })}
                    placeholder="https://linkedin.com/company/..."
                  />
                </div>
              </CardContent>
            </Card>

            {/* Head Office Address */}
            <HeadOfficeAddressSection
              companyInfo={companyInfo}
              setCompanyInfo={setCompanyInfo}
              fieldErrors={fieldErrors}
              FieldError={FieldError}
            />

            {/* Primary Contact */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <User size={20} className="mr-2 text-blue-600" />
                  Primary Contact Details
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name <span className="text-red-500">*</span></Label>
                  <Input
                    value={companyInfo.primary_contact_name}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, primary_contact_name: e.target.value })}
                    placeholder="Contact person's full name"
                    className={fieldErrors.primary_contact_name ? 'border-red-500' : ''}
                  />
                  <FieldError fieldKey="primary_contact_name" />
                </div>
                <div>
                  <Label>Designation / Role <span className="text-red-500">*</span></Label>
                  <Input
                    value={companyInfo.primary_contact_designation}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, primary_contact_designation: e.target.value })}
                    placeholder="e.g., Managing Director"
                    className={fieldErrors.primary_contact_designation ? 'border-red-500' : ''}
                  />
                  <FieldError fieldKey="primary_contact_designation" />
                </div>
                <div>
                  <Label>Email Address <span className="text-red-500">*</span></Label>
                  <Input
                    type="email"
                    value={companyInfo.primary_contact_email}
                    onChange={(e) => {
                      setCompanyInfo({ ...companyInfo, primary_contact_email: e.target.value });
                      clearFieldError('primary_contact_email');
                    }}
                    placeholder="email@company.com"
                    className={fieldErrors.primary_contact_email ? 'border-red-500' : ''}
                  />
                  <FieldError fieldKey="primary_contact_email" />
                </div>
                <div>
                  <Label>Mobile Number <span className="text-red-500">*</span></Label>
                  <div className="flex space-x-2">
                    <select
                      className="w-28 px-2 py-2 border border-input rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-gray-800"
                      value={companyInfo.primary_contact_country_code}
                      onChange={(e) => setCompanyInfo({ ...companyInfo, primary_contact_country_code: e.target.value })}
                      aria-label="Country code"
                    >
                      {phoneCodeOptions.map((opt) => (
                        <option key={opt.code} value={opt.code}>{opt.label}</option>
                      ))}
                    </select>
                    <Input
                      className="flex-1"
                      value={companyInfo.primary_contact_mobile}
                      onChange={(e) => setCompanyInfo({ ...companyInfo, primary_contact_mobile: e.target.value })}
                      placeholder="Mobile number"
                    />
                  </div>
                  <FieldError fieldKey="primary_contact_mobile" />
                </div>
              </CardContent>
            </Card>

            {/* Secondary Contact */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <User size={20} className="mr-2 text-gray-400" />
                  Secondary Contact (Optional)
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name</Label>
                  <Input
                    value={companyInfo.secondary_contact_name}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, secondary_contact_name: e.target.value })}
                    placeholder="Contact person's full name"
                  />
                </div>
                <div>
                  <Label>Designation</Label>
                  <Input
                    value={companyInfo.secondary_contact_designation}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, secondary_contact_designation: e.target.value })}
                    placeholder="Role in company"
                  />
                </div>
                <div>
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    value={companyInfo.secondary_contact_email}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, secondary_contact_email: e.target.value })}
                    placeholder="email@company.com"
                  />
                </div>
                <div>
                  <Label>Mobile Number</Label>
                  <div className="flex space-x-2">
                    <select
                      className="w-28 px-2 py-2 border border-input rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-gray-800"
                      value={companyInfo.secondary_contact_country_code}
                      onChange={(e) => setCompanyInfo({ ...companyInfo, secondary_contact_country_code: e.target.value })}
                      aria-label="Country code"
                    >
                      {phoneCodeOptions.map((opt) => (
                        <option key={opt.code} value={opt.code}>{opt.label}</option>
                      ))}
                    </select>
                    <Input
                      className="flex-1"
                      value={companyInfo.secondary_contact_mobile}
                      onChange={(e) => setCompanyInfo({ ...companyInfo, secondary_contact_mobile: e.target.value })}
                      placeholder="Mobile number"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Company Profile */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Briefcase size={20} className="mr-2 text-blue-600" />
                  Company Profile
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Total Number of Employees <span className="text-red-500">*</span></Label>
                  <Input
                    type="number"
                    value={companyInfo.total_employees}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, total_employees: e.target.value })}
                    placeholder="e.g., 50"
                    className={fieldErrors.total_employees ? 'border-red-500' : ''}
                  />
                  <FieldError fieldKey="total_employees" />
                </div>
                <div>
                  <Label>Languages Spoken <span className="text-red-500">*</span></Label>
                  {(() => {
                    const selected = Array.isArray(companyInfo.languages_spoken) ? companyInfo.languages_spoken : (companyInfo.languages_spoken ? [companyInfo.languages_spoken] : []);
                    const toggleLang = (lang) => {
                      const updated = selected.includes(lang) ? selected.filter(l => l !== lang) : [...selected, lang];
                      setCompanyInfo({ ...companyInfo, languages_spoken: updated });
                    };
                    return (
                      <div className="relative" ref={langDropdownRef}>
                        <div
                          className={`w-full min-h-[42px] px-3 py-2 border rounded-md cursor-pointer flex flex-wrap items-center gap-1 ${fieldErrors.languages_spoken ? 'border-red-500' : 'border-input'}`}
                          onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                        >
                          {selected.length === 0 && <span className="text-gray-400">Select languages</span>}
                          {selected.map((lang) => (
                            <Badge key={lang} variant="secondary" className="flex items-center gap-1 text-xs">
                              {lang}
                              <X size={12} className="cursor-pointer hover:text-red-500" onClick={(e) => { e.stopPropagation(); toggleLang(lang); }} />
                            </Badge>
                          ))}
                          <ChevronDown size={16} className="ml-auto text-gray-400 shrink-0" />
                        </div>
                        {langDropdownOpen && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {LANGUAGES_SPOKEN.map((lang) => (
                              <label key={lang} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                                <input
                                  type="checkbox"
                                  checked={selected.includes(lang)}
                                  onChange={() => toggleLang(lang)}
                                  className="rounded border-gray-300"
                                />
                                {lang}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <FieldError fieldKey="languages_spoken" />
                </div>
                <div>
                  <Label>Operating Days & Hours</Label>
                  <Input
                    value={companyInfo.operating_hours}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, operating_hours: e.target.value })}
                    placeholder="e.g., Mon-Fri, 9AM-6PM"
                  />
                </div>
                <div>
                  <Label>Key Airports / Ports Covered <span className="text-red-500">*</span></Label>
                  <Input
                    value={companyInfo.key_ports_airports}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, key_ports_airports: e.target.value })}
                    placeholder="e.g., LAX, Long Beach, JFK"
                    className={fieldErrors.key_ports_airports ? 'border-red-500' : ''}
                  />
                  <FieldError fieldKey="key_ports_airports" />
                </div>
                <div className="md:col-span-2">
                  <Label>Service Capabilities <span className="text-red-500">*</span></Label>
                  <Input
                    value={companyInfo.service_capabilities}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, service_capabilities: e.target.value })}
                    placeholder="e.g., Ocean FCL, Air Freight, Customs Brokerage"
                    className={fieldErrors.service_capabilities ? 'border-red-500' : ''}
                  />
                  <FieldError fieldKey="service_capabilities" />
                </div>
                <div className="md:col-span-2">
                  <Label>Company Strengths / Specialization <span className="text-red-500">*</span></Label>
                  {(() => {
                    const selected = Array.isArray(companyInfo.company_strengths) ? companyInfo.company_strengths : (companyInfo.company_strengths ? String(companyInfo.company_strengths).split(',').map(s => s.trim()).filter(Boolean) : []);
                    const toggleStrength = (item) => {
                      const updated = selected.includes(item) ? selected.filter(s => s !== item) : [...selected, item];
                      setCompanyInfo({ ...companyInfo, company_strengths: updated });
                    };
                    const filtered = COMPANY_STRENGTHS.filter(s => s.toLowerCase().includes(strengthsSearch.toLowerCase()));
                    return (
                      <div className="relative" ref={strengthsDropdownRef}>
                        <div
                          className={`w-full min-h-[42px] px-3 py-2 border rounded-md cursor-pointer flex flex-wrap items-center gap-1 ${fieldErrors.company_strengths ? 'border-red-500' : 'border-input'}`}
                          onClick={() => setStrengthsDropdownOpen(!strengthsDropdownOpen)}
                        >
                          {selected.length === 0 && <span className="text-gray-400">Select specializations</span>}
                          {selected.map((item) => (
                            <Badge key={item} variant="secondary" className="flex items-center gap-1 text-xs">
                              {item}
                              <X size={12} className="cursor-pointer hover:text-red-500" onClick={(e) => { e.stopPropagation(); toggleStrength(item); }} />
                            </Badge>
                          ))}
                          <ChevronDown size={16} className="ml-auto text-gray-400 shrink-0" />
                        </div>
                        {strengthsDropdownOpen && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
                            <div className="p-2 border-b">
                              <Input
                                value={strengthsSearch}
                                onChange={(e) => setStrengthsSearch(e.target.value)}
                                placeholder="Search specializations..."
                                className="h-8 text-sm"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                            <div className="max-h-60 overflow-y-auto">
                              {filtered.length === 0 && (
                                <p className="px-3 py-2 text-sm text-gray-400">No matches found</p>
                              )}
                              {filtered.map((item) => (
                                <label key={item} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                                  <input
                                    type="checkbox"
                                    checked={selected.includes(item)}
                                    onChange={() => toggleStrength(item)}
                                    className="rounded border-gray-300"
                                  />
                                  {item}
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <FieldError fieldKey="company_strengths" />
                </div>
                <div>
                  <Label>Main Trade Lanes Served <span className="text-red-500">*</span></Label>
                  <MultiSelectDropdown
                    options={tradeLaneOptionsForDropdown}
                    value={selectedTradeLaneValues}
                    onChange={(values) => {
                      const normalizedValues = Array.isArray(values)
                        ? values.map((value) => String(value || '').trim()).filter(Boolean)
                        : [];
                      setCompanyInfo({ ...companyInfo, main_trade_lanes: normalizedValues.join(', ') });
                      clearFieldError('main_trade_lanes');
                    }}
                    placeholder="Search and select trade lanes"
                    error={!!fieldErrors.main_trade_lanes}
                  />
                  <FieldError fieldKey="main_trade_lanes" />
                </div>
                <div>
                  <Label>Markets to Develop Through Network <span className="text-red-500">*</span></Label>
                  <Input
                    value={companyInfo.markets_to_develop}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, markets_to_develop: e.target.value })}
                    placeholder="e.g., Africa, South America"
                    className={fieldErrors.markets_to_develop ? 'border-red-500' : ''}
                  />
                  <FieldError fieldKey="markets_to_develop" />
                </div>
              </CardContent>
            </Card>

            {/* File Uploads */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Upload size={20} className="mr-2 text-blue-600" />
                  Company Files
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className={fieldErrors.company_logo ? 'rounded-lg border border-red-500 p-2' : ''}>
                  <FileUploadField
                    label="Company Logo"
                    required={true}
                    fieldName="company_logo"
                    currentValue={companyInfo.company_logo}
                    onUpload={(file) => file ? handleFileUpload(file, 'company_logo', setCompanyInfo, companyInfo, () => clearFieldError('company_logo')) : setCompanyInfo({ ...companyInfo, company_logo: null })}
                    accept=".jpg,.jpeg,.png,.svg"
                    setter={setCompanyInfo}
                    currentState={companyInfo}
                  />
                  <FieldError fieldKey="company_logo" />
                </div>
                <FileUploadField
                  label="Company Profile / Brochure"
                  required={false}
                  fieldName="company_brochure"
                  currentValue={companyInfo.company_brochure}
                  onUpload={(file) => file ? handleFileUpload(file, 'company_brochure', setCompanyInfo, companyInfo) : setCompanyInfo({ ...companyInfo, company_brochure: null })}
                  accept=".pdf"
                  setter={setCompanyInfo}
                  currentState={companyInfo}
                />
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 2: Branch Offices */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center text-lg">
                    <Building size={20} className="mr-2 text-blue-600" />
                    Branch Offices
                  </CardTitle>
                  <Button onClick={addBranch} variant="outline" size="sm">
                    <Plus size={16} className="mr-2" />
                    Add Branch
                  </Button>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Branch offices are optional. Add your additional locations if applicable.
                </p>
              </CardHeader>
              <CardContent>
                {branches.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <Building size={48} className="mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">No branch offices added</p>
                    <Button onClick={addBranch} variant="outline" className="mt-4">
                      <Plus size={16} className="mr-2" />
                      Add Your First Branch
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {branches.map((branch, index) => (
                      <div key={index} className="p-4 border rounded-lg bg-gray-50">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-medium">Branch {index + 1}</h4>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeBranch(index)}
                            className="text-red-500"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label>Branch Name <span className="text-red-500">*</span></Label>
                            <Input
                              value={branch.branch_name}
                              onChange={(e) => updateBranch(index, 'branch_name', e.target.value)}
                              placeholder="e.g., Shanghai Office"
                            />
                          </div>
                          <div>
                            <Label>Branch Type <span className="text-red-500">*</span></Label>
                            <select
                              className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-1 focus:ring-gray-800"
                              value={branch.branch_type}
                              onChange={(e) => updateBranch(index, 'branch_type', e.target.value)}
                            >
                              <option value="">Select type</option>
                              {BRANCH_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </div>
                          <div className="md:col-span-2">
                            <Label>Street Address <span className="text-red-500">*</span></Label>
                            <Input
                              value={branch.street_address}
                              onChange={(e) => updateBranch(index, 'street_address', e.target.value)}
                              placeholder="Branch address"
                            />
                          </div>
                          <BranchAddressFields branch={branch} index={index} updateBranch={updateBranch} />
                          <div className="md:col-span-2 pt-2 border-t">
                            <p className="text-sm font-medium text-gray-700 mb-3">Branch Contact</p>
                          </div>
                          <div>
                            <Label>Contact Person <span className="text-red-500">*</span></Label>
                            <Input
                              value={branch.contact_name}
                              onChange={(e) => updateBranch(index, 'contact_name', e.target.value)}
                              placeholder="Full name"
                            />
                          </div>
                          <div>
                            <Label>Designation</Label>
                            <Input
                              value={branch.contact_designation}
                              onChange={(e) => updateBranch(index, 'contact_designation', e.target.value)}
                              placeholder="Role"
                            />
                          </div>
                          <div>
                            <Label>Email <span className="text-red-500">*</span></Label>
                            <Input
                              type="email"
                              value={branch.contact_email}
                              onChange={(e) => updateBranch(index, 'contact_email', e.target.value)}
                              placeholder="email@company.com"
                              className={fieldErrors[`branch_${index}_contact_email`] ? 'border-red-500' : ''}
                            />
                            {fieldErrors[`branch_${index}_contact_email`] && (
                              <p className="mt-1 text-sm text-red-500">{fieldErrors[`branch_${index}_contact_email`]}</p>
                            )}
                          </div>
                          <div>
                            <Label>Mobile <span className="text-red-500">*</span></Label>
                            <div className="flex space-x-2">
                              <select
                                className="w-24 px-2 py-2 border border-input rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-gray-800"
                                value={branch.contact_country_code}
                                onChange={(e) => updateBranch(index, 'contact_country_code', e.target.value)}
                              >
                                {phoneCodeOptions.map((opt) => <option key={opt.code} value={opt.code}>{opt.code}</option>)}
                              </select>
                              <Input
                                className="flex-1"
                                value={branch.contact_mobile}
                                onChange={(e) => updateBranch(index, 'contact_mobile', e.target.value)}
                                placeholder="Mobile"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 3: Financial Information */}
        {currentStep === 3 && (
          <div className="space-y-6">
            {/* Financial Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <DollarSign size={20} className="mr-2 text-blue-600" />
                  Financial Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Years in Operation <span className="text-red-500">*</span></Label>
                  <Input
                    type="number"
                    value={financialInfo.years_in_operation}
                    onChange={(e) => setFinancialInfo({ ...financialInfo, years_in_operation: e.target.value })}
                    placeholder="e.g., 15"
                    className={fieldErrors.years_in_operation ? 'border-red-500' : ''}
                  />
                  <FieldError fieldKey="years_in_operation" />
                </div>
                <div>
                  <Label>Last Fiscal Year Turnover <span className="text-red-500">*</span></Label>
                  <div className="flex space-x-2">
                    <select
                      className="w-24 px-2 py-2 border border-input rounded-md focus:outline-none focus:ring-1 focus:ring-gray-800"
                      value={financialInfo.turnover_currency}
                      onChange={(e) => setFinancialInfo({ ...financialInfo, turnover_currency: e.target.value })}
                    >
                      {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <Input
                      type="number"
                      className={`flex-1 ${fieldErrors.last_fiscal_turnover ? 'border-red-500' : ''}`}
                      value={financialInfo.last_fiscal_turnover}
                      onChange={(e) => setFinancialInfo({ ...financialInfo, last_fiscal_turnover: e.target.value })}
                      placeholder="Annual turnover"
                    />
                  </div>
                  <FieldError fieldKey="last_fiscal_turnover" />
                </div>
                <div className="md:col-span-2">
                  <Label>Existing Memberships in Other Networks <span className="text-red-500">*</span></Label>
                  <Input
                    value={financialInfo.existing_memberships}
                    onChange={(e) => setFinancialInfo({ ...financialInfo, existing_memberships: e.target.value })}
                    placeholder="e.g., WCA, FIATA, None"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>History of Disputes? <span className="text-red-500">*</span></Label>
                  <div className="flex space-x-4 mt-2">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="disputes"
                        checked={financialInfo.history_of_disputes === 'no'}
                        onChange={() => setFinancialInfo({ ...financialInfo, history_of_disputes: 'no', dispute_reasons: '' })}
                        className="mr-2"
                      />
                      No
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="disputes"
                        checked={financialInfo.history_of_disputes === 'yes'}
                        onChange={() => setFinancialInfo({ ...financialInfo, history_of_disputes: 'yes' })}
                        className="mr-2"
                      />
                      Yes
                    </label>
                  </div>
                </div>
                {financialInfo.history_of_disputes === 'yes' && (
                  <div className="md:col-span-2">
                    <Label>Reasons for Dispute <span className="text-red-500">*</span></Label>
                    <Textarea
                      value={financialInfo.dispute_reasons}
                      onChange={(e) => setFinancialInfo({ ...financialInfo, dispute_reasons: e.target.value })}
                      placeholder="Please explain the nature and resolution of any disputes..."
                      rows={3}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Bank Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Building2 size={20} className="mr-2 text-blue-600" />
                  Bank Information
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Bank Name <span className="text-red-500">*</span></Label>
                  <Input
                    value={financialInfo.bank_name}
                    onChange={(e) => setFinancialInfo({ ...financialInfo, bank_name: e.target.value })}
                    placeholder="e.g., HSBC"
                    className={fieldErrors.bank_name ? 'border-red-500' : ''}
                  />
                  <FieldError fieldKey="bank_name" />
                </div>
                <div>
                  <Label>Bank Branch Address <span className="text-red-500">*</span></Label>
                  <Input
                    value={financialInfo.bank_branch_address}
                    onChange={(e) => setFinancialInfo({ ...financialInfo, bank_branch_address: e.target.value })}
                    placeholder="Branch location"
                  />
                </div>
                <div>
                  <Label>Bank Account Number <span className="text-red-500">*</span></Label>
                  <Input
                    value={financialInfo.account_number}
                    onChange={(e) => setFinancialInfo({ ...financialInfo, account_number: e.target.value })}
                    placeholder="Account number"
                    className={fieldErrors.account_number ? 'border-red-500' : ''}
                  />
                  <FieldError fieldKey="account_number" />
                </div>
                <div>
                  <Label>SWIFT / IBAN / Routing Number <span className="text-red-500">*</span></Label>
                  <Input
                    value={financialInfo.swift_iban}
                    onChange={(e) => setFinancialInfo({ ...financialInfo, swift_iban: e.target.value })}
                    placeholder="e.g., HSBCHKHHHKH"
                    className={fieldErrors.swift_iban ? 'border-red-500' : ''}
                  />
                  <FieldError fieldKey="swift_iban" />
                </div>
                <div>
                  <Label>Account Holder Name <span className="text-red-500">*</span></Label>
                  <Input
                    value={financialInfo.account_holder_name}
                    onChange={(e) => setFinancialInfo({ ...financialInfo, account_holder_name: e.target.value })}
                    placeholder="As registered with bank"
                    className={fieldErrors.account_holder_name ? 'border-red-500' : ''}
                  />
                  <FieldError fieldKey="account_holder_name" />
                </div>
              </CardContent>
            </Card>

            {/* Trade References */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center text-lg">
                    <Users size={20} className="mr-2 text-blue-600" />
                    Trade References
                  </CardTitle>
                  <Button onClick={addTradeReference} variant="outline" size="sm">
                    <Plus size={16} className="mr-2" />
                    Add Reference
                  </Button>
                </div>
                <p className="text-sm text-gray-500 mt-1">Minimum 2 Industry References are required.</p>
                <FieldError fieldKey="trade_references_min" />
              </CardHeader>
              <CardContent className="space-y-4">
                {tradeReferences.map((ref, index) => (
                  <div key={index} className="p-4 border rounded-lg bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-sm">Reference {index + 1}</h4>
                      {index >= MIN_INDUSTRY_REFERENCES && (
                        <Button variant="ghost" size="sm" onClick={() => removeTradeReference(index)} className="text-red-500">
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Company Name {index < MIN_INDUSTRY_REFERENCES && <span className="text-red-500">*</span>}</Label>
                        <Input
                          value={ref.company_name}
                          onChange={(e) => updateTradeReference(index, 'company_name', e.target.value)}
                          placeholder="Reference company"
                          className={index < MIN_INDUSTRY_REFERENCES && fieldErrors[`trade_ref_${index}_company_name`] ? 'border-red-500' : ''}
                        />
                        {index < MIN_INDUSTRY_REFERENCES && <FieldError fieldKey={`trade_ref_${index}_company_name`} />}
                      </div>
                      <div>
                        <Label className="text-xs">Contact Person {index < MIN_INDUSTRY_REFERENCES && <span className="text-red-500">*</span>}</Label>
                        <Input
                          value={ref.contact_person}
                          onChange={(e) => updateTradeReference(index, 'contact_person', e.target.value)}
                          placeholder="Full name"
                          className={index < MIN_INDUSTRY_REFERENCES && fieldErrors[`trade_ref_${index}_contact_person`] ? 'border-red-500' : ''}
                        />
                        {index < MIN_INDUSTRY_REFERENCES && <FieldError fieldKey={`trade_ref_${index}_contact_person`} />}
                      </div>
                      <div>
                        <Label className="text-xs">Email {index < MIN_INDUSTRY_REFERENCES && <span className="text-red-500">*</span>}</Label>
                        <Input
                          type="email"
                          value={ref.email}
                          onChange={(e) => updateTradeReference(index, 'email', e.target.value)}
                          placeholder="email@company.com"
                          className={index < MIN_INDUSTRY_REFERENCES && fieldErrors[`trade_ref_${index}_email`] ? 'border-red-500' : ''}
                        />
                        {index < MIN_INDUSTRY_REFERENCES && <FieldError fieldKey={`trade_ref_${index}_email`} />}
                      </div>
                      <div>
                        <Label className="text-xs">Mobile {index < MIN_INDUSTRY_REFERENCES && <span className="text-red-500">*</span>}</Label>
                        <div className="flex space-x-2">
                          <select
                            className="w-20 px-1 py-2 border border-input rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-gray-800"
                            value={ref.country_code}
                            onChange={(e) => updateTradeReference(index, 'country_code', e.target.value)}
                          >
                            {phoneCodeOptions.map((opt) => <option key={opt.code} value={opt.code}>{opt.code}</option>)}
                          </select>
                          <Input
                            className={`flex-1 ${index < MIN_INDUSTRY_REFERENCES && fieldErrors[`trade_ref_${index}_mobile`] ? 'border-red-500' : ''}`}
                            value={ref.mobile}
                            onChange={(e) => updateTradeReference(index, 'mobile', e.target.value)}
                            placeholder="Mobile"
                          />
                        </div>
                        {index < MIN_INDUSTRY_REFERENCES && <FieldError fieldKey={`trade_ref_${index}_mobile`} />}
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-xs">Relationship Duration {index < MIN_INDUSTRY_REFERENCES && <span className="text-red-500">*</span>}</Label>
                        <Input
                          value={ref.relationship_duration}
                          onChange={(e) => updateTradeReference(index, 'relationship_duration', e.target.value)}
                          placeholder="e.g., 5 years"
                          className={index < MIN_INDUSTRY_REFERENCES && fieldErrors[`trade_ref_${index}_relationship_duration`] ? 'border-red-500' : ''}
                        />
                        {index < MIN_INDUSTRY_REFERENCES && <FieldError fieldKey={`trade_ref_${index}_relationship_duration`} />}
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 4: Compliance & Verification */}
        {currentStep === 4 && (
          <div className="space-y-6">
            {/* Compliance Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Shield size={20} className="mr-2 text-blue-600" />
                  Compliance Details
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Tax Registration / VAT / GST Number <span className="text-red-500">*</span></Label>
                  <Input
                    value={complianceInfo.tax_registration_number}
                    onChange={(e) => {
                      setComplianceInfo({ ...complianceInfo, tax_registration_number: e.target.value });
                      clearFieldError('tax_registration_number');
                    }}
                    placeholder="Tax ID"
                    className={fieldErrors.tax_registration_number ? 'border-red-500' : ''}
                  />
                  <FieldError fieldKey="tax_registration_number" />
                </div>
                <div>
                  <Label>LEI Number (Legal Entity Identifier)</Label>
                  <Input
                    value={complianceInfo.lei_number}
                    onChange={(e) => setComplianceInfo({ ...complianceInfo, lei_number: e.target.value })}
                    placeholder="20-character LEI (optional)"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Mandatory Documents */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <FileText size={20} className="mr-2 text-red-600" />
                  Mandatory Documents
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className={fieldErrors.financial_statement ? 'rounded-lg border border-red-500 p-2' : ''}>
                  <FileUploadField
                    label="Latest Tax Audited Financial Statement"
                    required={true}
                    fieldName="financial_statement"
                    currentValue={complianceInfo.financial_statement}
                    onUpload={(file) => file ? handleFileUpload(file, 'financial_statement', setComplianceInfo, complianceInfo, () => clearFieldError('financial_statement')) : setComplianceInfo({ ...complianceInfo, financial_statement: null })}
                    setter={setComplianceInfo}
                    currentState={complianceInfo}
                  />
                  <FieldError fieldKey="financial_statement" />
                </div>
                <div className={fieldErrors.registration_certificate ? 'rounded-lg border border-red-500 p-2' : ''}>
                  <FileUploadField
                    label="Company Registration Certificate"
                    required={true}
                    fieldName="registration_certificate"
                    currentValue={complianceInfo.registration_certificate}
                    onUpload={(file) => file ? handleFileUpload(file, 'registration_certificate', setComplianceInfo, complianceInfo, () => clearFieldError('registration_certificate')) : setComplianceInfo({ ...complianceInfo, registration_certificate: null })}
                    setter={setComplianceInfo}
                    currentState={complianceInfo}
                  />
                  <FieldError fieldKey="registration_certificate" />
                </div>
              </CardContent>
            </Card>

            {/* Optional Documents */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <FileText size={20} className="mr-2 text-gray-400" />
                  Optional Documents
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FileUploadField
                  label="IATA / FIATA / ISO Certifications"
                  required={false}
                  fieldName="iata_fiata_iso"
                  currentValue={complianceInfo.iata_fiata_iso}
                  onUpload={(file) => file ? handleFileUpload(file, 'iata_fiata_iso', setComplianceInfo, complianceInfo) : setComplianceInfo({ ...complianceInfo, iata_fiata_iso: null })}
                  setter={setComplianceInfo}
                  currentState={complianceInfo}
                />
                <FileUploadField
                  label="Business License"
                  required={false}
                  fieldName="business_license"
                  currentValue={complianceInfo.business_license}
                  onUpload={(file) => file ? handleFileUpload(file, 'business_license', setComplianceInfo, complianceInfo) : setComplianceInfo({ ...complianceInfo, business_license: null })}
                  setter={setComplianceInfo}
                  currentState={complianceInfo}
                />
                <FileUploadField
                  label="DG Certification"
                  required={false}
                  fieldName="dg_certification"
                  currentValue={complianceInfo.dg_certification}
                  onUpload={(file) => file ? handleFileUpload(file, 'dg_certification', setComplianceInfo, complianceInfo) : setComplianceInfo({ ...complianceInfo, dg_certification: null })}
                  setter={setComplianceInfo}
                  currentState={complianceInfo}
                />
                <FileUploadField
                  label="Insurance Certificate (FFL, Warehouse, etc.)"
                  required={false}
                  fieldName="insurance_certificate"
                  currentValue={complianceInfo.insurance_certificate}
                  onUpload={(file) => file ? handleFileUpload(file, 'insurance_certificate', setComplianceInfo, complianceInfo) : setComplianceInfo({ ...complianceInfo, insurance_certificate: null })}
                  setter={setComplianceInfo}
                  currentState={complianceInfo}
                />
              </CardContent>
            </Card>

            {/* Declarations */}
            <Card className="border-2 border-amber-200 bg-amber-50/50">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <AlertCircle size={20} className="mr-2 text-amber-600" />
                  Declarations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { key: 'info_accuracy_confirmed', text: 'I confirm that all information provided in this application is accurate and complete to the best of my knowledge.' },
                  { key: 'no_fraud_confirmed', text: 'I confirm that our company has not been involved in any fraudulent activities or serious regulatory violations.' },
                  { key: 'approval_process_acknowledged', text: 'I acknowledge that this application is subject to review and approval by One Globe Alliance, and membership is not guaranteed.' },
                  { key: 'network_rules_agreed', text: 'I agree to abide by the rules, regulations, and code of conduct of the One Globe Alliance network upon approval.' },
                ].map((declaration) => (
                  <div key={declaration.key} className={fieldErrors[declaration.key] ? 'rounded-lg border border-red-500 p-3' : ''}>
                    <label className="flex items-start space-x-3 cursor-pointer p-3 rounded-lg hover:bg-amber-100/50 transition-colors">
                      <input
                        type="checkbox"
                        checked={complianceInfo[declaration.key]}
                        onChange={(e) => {
                          setComplianceInfo({ ...complianceInfo, [declaration.key]: e.target.checked });
                          clearFieldError(declaration.key);
                        }}
                        className="mt-1 w-5 h-5 rounded border-amber-400"
                      />
                      <span className="text-sm text-gray-700">{declaration.text} <span className="text-red-500">*</span></span>
                    </label>
                    <FieldError fieldKey={declaration.key} />
                  </div>
                ))}
                <label className="flex items-start space-x-3 cursor-pointer rounded-lg border border-blue-200 bg-blue-50 p-3 hover:bg-blue-100/60 transition-colors">
                  <input
                    type="checkbox"
                    checked={complianceInfo.linkedin_onboarding_consent || false}
                    onChange={(e) => setComplianceInfo({ ...complianceInfo, linkedin_onboarding_consent: e.target.checked })}
                    className="mt-1 w-5 h-5 rounded border-blue-400"
                  />
                  <span className="text-sm text-gray-700">I consent to One Globe Alliance sharing this company&apos;s onboarding announcement on its LinkedIn page. <span className="text-gray-500">Optional.</span></span>
                </label>
              </CardContent>
            </Card>

            {/* Applicant Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <User size={20} className="mr-2 text-blue-600" />
                  Applicant Details
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Applicant Name <span className="text-red-500">*</span></Label>
                  <Input
                    value={complianceInfo.applicant_name}
                    onChange={(e) => {
                      setComplianceInfo({ ...complianceInfo, applicant_name: e.target.value });
                      clearFieldError('applicant_name');
                    }}
                    placeholder="Person submitting this application"
                    className={fieldErrors.applicant_name ? 'border-red-500' : ''}
                  />
                  <FieldError fieldKey="applicant_name" />
                </div>
                <div>
                  <Label>Designation <span className="text-red-500">*</span></Label>
                  <Input
                    value={complianceInfo.applicant_designation}
                    onChange={(e) => {
                      setComplianceInfo({ ...complianceInfo, applicant_designation: e.target.value });
                      clearFieldError('applicant_designation');
                    }}
                    placeholder="Your role in the company"
                    className={fieldErrors.applicant_designation ? 'border-red-500' : ''}
                  />
                  <FieldError fieldKey="applicant_designation" />
                </div>
              </CardContent>
            </Card>

          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
            className="w-32"
          >
            <ChevronLeft size={18} className="mr-2" />
            Back
          </Button>

          <div className="flex items-center space-x-3">
            <div className="text-xs text-gray-500 min-w-[180px] text-right">
              {draftSaveStatus === 'saving' && 'Saving...'}
              {draftSaveStatus === 'saved' && (
                lastSavedAt
                  ? `Draft Saved • Last saved at ${lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                  : 'Draft Saved'
              )}
              {draftSaveStatus === 'error' && 'Save failed. Retrying...'}
              {draftSaveStatus === 'idle' && autosaveDirtyRef.current && 'Unsaved changes'}
            </div>
            <Button
              variant="outline"
              onClick={handleSaveDraft}
              disabled={savingDraft || !(companyInfo.primary_contact_email || getUserEmail())}
              className="w-40"
            >
              {savingDraft ? (
                <>
                  <Loader2 size={18} className="mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} className="mr-2" />
                  Save as Draft
                </>
              )}
            </Button>
            {currentStep < 4 ? (
              <Button onClick={handleNext} disabled={validatingNext} className="w-32 bg-blue-600 hover:bg-blue-700 text-white">
                {validatingNext ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Checking...
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight size={18} className="ml-2" />
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="w-48 bg-green-600 hover:bg-green-700 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Check size={18} className="mr-2" />
                    Submit Application
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyRegistration;
